// client/src/components/creatures/CreatureDetailModal.js
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CreatureDetailModal = ({ open, onClose, creature }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!creature) {
    return null; // Don't render if no creature data is provided
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm" // Smaller max-width for creature details
      fullWidth
      fullScreen={isMobile} // Fullscreen on mobile, controlled width on desktop
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" }, // No border radius on fullscreen mobile
          background: "rgba(255, 255, 255, 0.9)", // Slightly translucent white background
          backdropFilter: "blur(10px)", // Frosted glass effect
          WebkitBackdropFilter: "blur(10px)", // For Safari
          border: "1px solid rgba(255, 255, 255, 0.5)", // Subtle border
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)", // Soft shadow
          overflow: "hidden", // Ensure no scrollbars from overflow on Paper
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1, // Reduce right padding to pull close button closer
          pt: 1, // Reduce top padding
          pb: 0.5, // Reduce bottom padding
          background:
            "linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)", // Matches header in CreatureListTile
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          color: theme.palette.primary.main, // Primary color for title text
        }}
      >
        <Typography
          variant="h6"
          component="div" // Use div to allow more control over alignment
          sx={{
            flexGrow: 1,
            fontWeight: 700,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            py: 1, // Add vertical padding to the title itself
            ml: 2, // Push text away from edge slightly
          }}
        >
          {creature.commonName || "Creature Details"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.palette.grey[500],
            "&:hover": {
              color: theme.palette.grey[700],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {creature.imageUrl && (
          <Box
            component="img"
            src={creature.imageUrl}
            alt={creature.commonName}
            sx={{
              width: "100%",
              height: isMobile ? "200px" : "250px", // Responsive image height
              objectFit: "cover",
              display: "block",
              mb: 2, // Margin below image
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          />
        )}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {" "}
          {/* Responsive padding for content */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            color="text.primary"
          >
            Scientific Name:{" "}
            <Typography
              component="span"
              variant="body1"
              fontStyle="italic"
              color="text.secondary"
            >
              {creature.scientificName}
            </Typography>
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            color="text.primary"
          >
            Category:{" "}
            <Typography component="span" variant="body1" color="text.secondary">
              {creature.category}
            </Typography>
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            color="text.primary"
          >
            Species:{" "}
            <Typography component="span" variant="body1" color="text.secondary">
              {creature.species}
            </Typography>
          </Typography>
          <Typography variant="h6" mt={3} mb={1} color="text.primary">
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {creature.description}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatureDetailModal;
